import {
  useDeleteWorkspaceMutation,
  useGetWorkspaceMembersQuery,
  useGetWorkspaceQuery,
  useLeaveWorkspaceMutation,
  useUpdateWorkspaceMutation,
} from '@carboncopyco/data';
import {
  AdminToolsIcon,
  ArchiveIcon,
  DeleteOutlineIcon,
  GroupAddIcon,
  LinkIcon,
  MoreVerticalOutlineIcon,
  SettingsIcon,
  SignoutOutlineIcon,
} from '@carboncopyco/icons';
import { PermissionAction, PermissionKey } from '@carboncopyco/types';
import {
  Button,
  Dropdown,
  DropdownItem,
  Separator,
  TooltipBubble,
  notify,
} from '@carboncopyco/ui';
import { useWorkspaceAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  WorkspaceArchiveModal,
  WorkspaceLeaveModal,
  WorkspaceManageTagModal,
  WorkspaceSettingsModal,
  workspaceModalLabels,
} from '..';
import { useCopyUrl } from '../../../hooks/useCopyUrl';
import { usePermissionsContext } from '../../utils';
import { useWorkspacePageProps } from './WorkspacePageContext';

export const WorkspacePageDropdown = () => {
  const {
    goToAllWorkspaces,
    organizationId,
    workspaceId,
    handleWorkspaceMembersModal,
  } = useWorkspacePageProps();
  const { data: workspace } = useGetWorkspaceQuery({
    organizationId,
    id: workspaceId,
  });
  const navigate = useNavigate();
  const flags = useFlags();
  const { copyCurrentURL } = useCopyUrl();
  const goToArchivedWorkspaceAssets = () => {
    navigate('archive');
  };

  const { getPermissions } = usePermissionsContext();

  const archiveWorkspacePermission = getPermissions(
    PermissionKey.workspace,
    PermissionAction.deleteWorkspaceById
  );

  const userAllowedToEditWorkspace = getPermissions(
    PermissionKey.roles,
    PermissionAction.getRoles
  );
  const editWorkspacePermission = getPermissions(
    PermissionKey.workspace,
    PermissionAction.updateWorkspaceById
  );
  const [archiveModalLabel, setArchiveModalLabel] = useState(
    workspaceModalLabels.archive
  );
  const [
    isManageTagsOpen,
    { setTrue: setTrueIsManageTagsModal, setFalse: setFalseIsManageTagsModal },
  ] = useBoolean();
  const [
    showArchiveModal,
    { setTrue: setTrueShowArchiveModal, setFalse: setFalseShowArchiveModal },
  ] = useBoolean();
  const [
    showLeaveModal,
    { setTrue: setTrueShowLeaveModal, setFalse: setFalseShowLeaveModal },
  ] = useBoolean();
  const [
    showSettingsModal,
    { setTrue: setTrueShowSettingsModal, setFalse: setFalseShowSettingsModal },
  ] = useBoolean();

  const [archiveWorkspace] = useDeleteWorkspaceMutation();
  const [leaveWorkspace] = useLeaveWorkspaceMutation();
  const [updateWorkspace, { isLoading: isLoadingUpdateWorkspace }] =
    useUpdateWorkspaceMutation();
  const { data: members } = useGetWorkspaceMembersQuery({
    organizationId,
    workspaceId,
  });
  const totalMembers = members?.metadata.totalItems;
  const { trackWorkspaceClickLeave, trackWorkspaceClickApproveArchive } =
    useWorkspaceAnalytics('workspace_page');

  const onLeaveWorkspace = () => {
    if (workspace?.workspaceType === 'private' && totalMembers === 1) {
      setArchiveModalLabel(workspaceModalLabels.leaveLastMember);
      setTrueShowArchiveModal();
      return;
    }
    setTrueShowLeaveModal();
  };

  const allowToTagWorkspace = getPermissions(
    PermissionKey.tag,
    PermissionAction.tagFolder
  );

  const isDrafts = workspace?.workspaceType === 'playground';
  const isDefault = workspace?.workspaceType === 'default';

  return workspace ? (
    <>
      <Dropdown
        asChild
        arrow
        align="center"
        triggerElement={
          <Button
            aria-label="menu"
            blue
            secondary
            icon={<MoreVerticalOutlineIcon />}
          />
        }
      >
        <div className="p-1">
          {!isDrafts && (
            <TooltipBubble
              placement="top"
              title="All organization members are part of the default workspace"
              disabled={!isDefault}
            >
              <DropdownItem
                label="Members"
                iconLeft={<GroupAddIcon size={18} />}
                disabled={isDefault || !userAllowedToEditWorkspace}
                onClick={handleWorkspaceMembersModal}
              />
            </TooltipBubble>
          )}
          {editWorkspacePermission && !isDrafts && (
            <DropdownItem
              label="Edit settings"
              iconLeft={<AdminToolsIcon size={18} />}
              disabled={!userAllowedToEditWorkspace}
              onClick={setTrueShowSettingsModal}
            />
          )}
          {archiveWorkspacePermission && (
            <DropdownItem
              label="View archived items"
              iconLeft={<ArchiveIcon size={18} />}
              onClick={goToArchivedWorkspaceAssets}
            />
          )}
          {flags.partners && allowToTagWorkspace && (
            <DropdownItem
              iconLeft={<SettingsIcon size={18} />}
              label="Manage tags"
              onClick={setTrueIsManageTagsModal}
            />
          )}
          {flags.copyUrl && (
            <DropdownItem
              iconLeft={<LinkIcon size={18} />}
              label="Copy page URL"
              onClick={() => {
                copyCurrentURL();
              }}
            />
          )}
          {!isDrafts && (
            <>
              <div className="p-1">
                <Separator horizontal />
              </div>
              <TooltipBubble
                placement="top"
                title="Leaving the default workspace is not possible"
                disabled={!isDefault}
              >
                <DropdownItem
                  label="Leave workspace"
                  iconLeft={<SignoutOutlineIcon size={18} />}
                  onClick={onLeaveWorkspace}
                  disabled={isDefault}
                />
              </TooltipBubble>
            </>
          )}
          {archiveWorkspacePermission && !isDrafts && (
            <>
              <div className="p-1">
                <Separator horizontal />
              </div>
              <TooltipBubble
                placement="top"
                title="The default workspace can't be archived"
                disabled={!isDefault}
              >
                <DropdownItem
                  label="Archive workspace"
                  iconLeft={<DeleteOutlineIcon size={18} />}
                  onClick={setTrueShowArchiveModal}
                  disabled={isDefault}
                />
              </TooltipBubble>
            </>
          )}
        </div>
      </Dropdown>
      {showArchiveModal && (
        <WorkspaceArchiveModal
          show={showArchiveModal}
          label={archiveModalLabel}
          onClose={setFalseShowArchiveModal}
          archiveWorkspace={() =>
            archiveWorkspace({
              organizationId,
              id: workspaceId,
            })
              .unwrap()
              .then(() => {
                trackWorkspaceClickApproveArchive();
                notify.success(
                  'Workspace has been archived successfully and its shared links have been deactivated'
                );
                goToAllWorkspaces();
              })
              .catch(() =>
                notify.error('There was an error archiving this workspace')
              )
              .finally(setFalseShowArchiveModal)
          }
        />
      )}
      {showLeaveModal && (
        <WorkspaceLeaveModal
          show={showLeaveModal}
          onClose={setFalseShowLeaveModal}
          leaveWorkspace={() =>
            leaveWorkspace({ organizationId, workspaceId })
              .unwrap()
              .then(() => {
                notify.success(
                  `You have left ${workspace.workspaceName} workspace`
                );
                trackWorkspaceClickLeave();
                goToAllWorkspaces();
              })
              .catch(() =>
                notify.error('There was an error leaving this workspace')
              )
              .finally(setFalseShowLeaveModal)
          }
        />
      )}
      {showSettingsModal && (
        <WorkspaceSettingsModal
          settings={{
            workspaceName: workspace.workspaceName,
            workspaceType: workspace.workspaceType,
            color: workspace.color,
          }}
          isOpen={showSettingsModal}
          handleSubmit={(settings) => {
            const { workspaceType, ...rest } = settings;
            const workspaceSettings =
              workspaceType === 'default' ? rest : settings;

            updateWorkspace({
              organizationId,
              id: workspaceId,
              ...workspaceSettings,
            })
              .unwrap()
              .then(() => {
                notify.success('Workspace has been updated successfully');
              })
              .catch(() => {
                notify.error('Error updating workspace');
              })
              .finally(setFalseShowSettingsModal);
          }}
          handleClose={setFalseShowSettingsModal}
          isLoading={isLoadingUpdateWorkspace}
        />
      )}
      {isManageTagsOpen && (
        <WorkspaceManageTagModal
          isOpen={isManageTagsOpen}
          organizationId={organizationId}
          workspace={workspace}
          onClose={setFalseIsManageTagsModal}
        />
      )}
    </>
  ) : null;
};
